import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaCheck, FaCopy, FaTimes } from 'react-icons/fa';
import Header from '../Header';
import api from '../services/apiService';
import { useNotification } from '../components/NotificationProvider';
import { ACADEMY_ADMIN_API, ACADEMY_ROLE_SECRETARY, academiesForRole, getCachedAcademies, setCachedAcademies } from '../utils/academyAccess';
import { authService } from '../services/authService';
import { compareAcademyTeams } from '../utils/academyTeamSort';
import '../scss/membershipPlayers.scss';

const paidTone = (paid, total) => {
  if (total < 1) {
    return 'is-empty';
  }
  if (paid >= total) {
    return 'is-complete';
  }
  if (paid < 1) {
    return 'is-none';
  }
  return 'is-partial';
};

const monthTabName = (month) => String(month?.name || month?.year_month || '').replace(/\s+\d{4}$/, '');

const teamLabel = (team) => {
  const name = team.team_name || `Team ${team.team_id}`;
  const age = String(team.age_group || '').trim();
  if (age && !name.toLowerCase().includes(age.toLowerCase())) {
    return `${name} · ${age}`;
  }
  return name;
};

const AcademyPayments = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const tabListRef = useRef(null);
  const [academies, setAcademies] = useState(() => (
    authService.isSuperAdmin()
      ? getCachedAcademies()
      : academiesForRole(getCachedAcademies(), ACADEMY_ROLE_SECRETARY)
  ));
  const [loadingAcademies, setLoadingAcademies] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [academyName, setAcademyName] = useState('');
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [setupReady, setSetupReady] = useState(true);
  const [setupNote, setSetupNote] = useState('');

  useEffect(() => {
    let cancelled = false;
    const loadAcademies = async () => {
      try {
        const response = await api.get(`${ACADEMY_ADMIN_API}/get_my_academy_subscriptions.php`);
        if (cancelled) {
          return;
        }
        const list = response.data?.success ? (response.data.academies || []) : [];
        setCachedAcademies(list);
        setAcademies(authService.isSuperAdmin() ? list : academiesForRole(list, ACADEMY_ROLE_SECRETARY));
      } catch (err) {
        if (!cancelled) {
          const cached = getCachedAcademies();
          setAcademies(authService.isSuperAdmin() ? cached : academiesForRole(cached, ACADEMY_ROLE_SECRETARY));
        }
      } finally {
        if (!cancelled) {
          setLoadingAcademies(false);
        }
      }
    };
    loadAcademies();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (id || loadingAcademies || academies.length !== 1) {
      return;
    }
    navigate(`/academy-payments/${academies[0].id}`, { replace: true });
  }, [id, loadingAcademies, academies, navigate]);

  const loadPayments = useCallback(async () => {
    if (!id) {
      setMonths([]);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get(`${ACADEMY_ADMIN_API}/get_academy_payments.php?id=${id}`);
      const data = response.data;
      if (!data.success) {
        throw new Error(data.message || 'Could not load payments');
      }
      const nextMonths = Array.isArray(data.months) ? data.months : [];
      setAcademyName(data.academy_name || '');
      setSetupReady(data.setup_ready !== false);
      setSetupNote(data.setup_note || '');
      setMonths(nextMonths);
      const current = data.year_month || nextMonths[0]?.year_month || null;
      setSelectedMonth(current);
    } catch (err) {
      const denied = err.response?.status === 403;
      setError(denied
        ? 'Club Secretary access is required'
        : (err.response?.data?.message || err.message || 'Could not load payments'));
      setMonths([]);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  useEffect(() => {
    const list = tabListRef.current;
    if (!list || !selectedMonth) {
      return;
    }
    const active = list.querySelector('[aria-selected="true"]');
    if (active && active.scrollIntoView) {
      active.scrollIntoView({ inline: 'center', block: 'nearest' });
    }
  }, [selectedMonth, months]);

  const activeMonth = useMemo(
    () => months.find((month) => month.year_month === selectedMonth) || null,
    [months, selectedMonth]
  );

  const teams = useMemo(
    () => [...(activeMonth?.teams || [])].sort(compareAcademyTeams),
    [activeMonth]
  );

  const copyLink = async (monthKey, player) => {
    if (player.is_free || !player.payment_link) {
      return;
    }
    const url = player.payment_link;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (!ok) {
          throw new Error('Copy command failed');
        }
      }
      setCopiedKey(`${monthKey}-${player.team_id || ''}-${player.player_id}`);
      showSuccess('Payment link copied');
      window.setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      showError('Could not copy the payment link');
    }
  };

  const chooseAcademy = (event) => {
    const nextId = event.target.value;
    if (!nextId) {
      navigate('/academy-payments');
      return;
    }
    navigate(`/academy-payments/${nextId}`);
  };

  return (
    <div className="membership-players-container academy-payments container">
      <Header />
      <h1 className="page-title">Parent payments</h1>
      {academyName ? <p className="membership-meta academy-payments-name">{academyName}</p> : null}

      {!loadingAcademies && academies.length > 1 ? (
        <label className="academy-switcher">
          <span className="visually-hidden">Academy</span>
          <select value={id ? String(id) : ''} onChange={chooseAcademy}>
            <option value="">Choose an academy</option>
            {academies.map((academy) => (
              <option key={academy.id} value={String(academy.id)}>{academy.name}</option>
            ))}
          </select>
        </label>
      ) : null}

      {(loadingAcademies || isLoading) && <div className="loading">Loading payments…</div>}

      {!loadingAcademies && !isLoading && !id && academies.length === 0 && (
        <div className="error">No academy settings are assigned to this account.</div>
      )}

      {!loadingAcademies && !isLoading && !id && academies.length > 1 && (
        <div className="membership-empty academy-choose">Choose an academy to see parent payments.</div>
      )}

      {!isLoading && error && <div className="error">{error}</div>}

      {!isLoading && !error && id && (
        <>
          <div className="membership-summary">
            <p className={`membership-setup ${setupReady ? 'is-ready' : 'is-pending'}`}>
              {setupReady ? 'Set up' : 'Not set up'}
            </p>
            {!setupReady && setupNote ? (
              <p className="membership-setup-note">{setupNote}</p>
            ) : null}
          </div>

          {months.length === 0 ? (
            <div className="error">No subscription months found</div>
          ) : (
            <>
              <div
                className="membership-month-tabs"
                role="tablist"
                aria-label="Months"
                ref={tabListRef}
              >
                {months.map((month) => {
                  const paid = month.paid_count || 0;
                  const total = month.player_count || 0;
                  const isActive = selectedMonth === month.year_month;
                  const label = monthTabName(month);
                  return (
                    <button
                      key={month.year_month}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={isActive ? 'is-active' : ''}
                      onClick={() => setSelectedMonth(month.year_month)}
                    >
                      <span className="membership-tab-month">{label}</span>
                      <span className={`membership-tab-count ${paidTone(paid, total)}`}>
                        ({paid}/{total})
                      </span>
                    </button>
                  );
                })}
              </div>

              {teams.length === 0 ? (
                <div className="membership-empty">No teams are assigned to this academy.</div>
              ) : (
                teams.map((team) => {
                  const paid = team.paid_count || 0;
                  const total = team.player_count || 0;
                  const players = team.players || [];
                  return (
                    <section key={team.team_id} className="academy-team">
                      <h2 className="academy-team-heading">
                        <span>{teamLabel(team)}</span>
                        <span className={`membership-count ${paidTone(paid, total)}`}>
                          ({paid}/{total})
                        </span>
                      </h2>
                      {players.length === 0 ? (
                        <div className="membership-empty">No players found for this team</div>
                      ) : (
                        <ul className="membership-list">
                          {players.map((player) => (
                            <li
                              key={player.player_id}
                              className={`membership-row${player.is_free ? ' is-free' : ''}`}
                            >
                              <div className="membership-player">
                                <span className="membership-name">
                                  {player.player_name}
                                  {player.is_free ? <span className="membership-free">Free</span> : null}
                                </span>
                                {player.parent_name ? (
                                  <span className="membership-parent">{player.parent_name}</span>
                                ) : null}
                              </div>
                              <div className="membership-actions">
                                {player.is_free ? null : (
                                  <span
                                    className={`membership-status ${player.paid ? 'is-paid' : 'is-unpaid'}`}
                                    title={player.paid ? 'Paid' : 'Not paid'}
                                    aria-label={player.paid ? 'Paid' : 'Not paid'}
                                  >
                                    {player.paid ? <FaCheck /> : <FaTimes />}
                                  </span>
                                )}
                                <button
                                  type="button"
                                  className="membership-copy"
                                  onClick={() => copyLink(activeMonth.year_month, { ...player, team_id: team.team_id })}
                                  disabled={Boolean(player.is_free)}
                                  aria-label={
                                    player.is_free
                                      ? `${player.player_name} is free`
                                      : `Copy payment link for ${player.player_name}`
                                  }
                                >
                                  <FaCopy />
                                  <span>
                                    {copiedKey === `${activeMonth.year_month}-${team.team_id}-${player.player_id}`
                                      ? 'Copied'
                                      : 'Copy link'}
                                  </span>
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  );
                })
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AcademyPayments;
